import { Mongo } from 'meteor/mongo';
import {
  UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID,
  defaultFeeQuotePolicyId,
  feeQuotePolicyById,
} from './payments';

export const ProjectsCollection = new Mongo.Collection('projects');

export const PROJECT_STATUSES = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
});

export const SETTLEMENT_CADENCES = Object.freeze([
  'immediate',
  'daily',
  'weekly',
  'monthly',
]);
export const SUPPORTED_SETTLEMENT_CADENCES = Object.freeze(['immediate']);

export const DEFAULT_PROJECT_POLICY = Object.freeze({
  currency: 'CAD',
  settlementCadence: 'immediate',
  transferTax: true,
  payerProcessorFeeShareBps: 5000,
  operatorProcessorFeeShareBps: 5000,
  processorFeeQuotePolicyId: 'stripe-ca-domestic-card-2026-08',
  showPaymentProgressPercent: true,
});

export const ACTIVE_INVOICE_PAGE_SIZE = 20;

export function validateProjectPolicy(policy) {
  const currency = String(policy?.currency || '').trim().toUpperCase();
  const settlementCadence = String(policy?.settlementCadence || '');
  const payerProcessorFeeShareBps = Number(policy?.payerProcessorFeeShareBps);
  const operatorProcessorFeeShareBps = Number(policy?.operatorProcessorFeeShareBps);
  const processorFeeQuotePolicyId = String(policy?.processorFeeQuotePolicyId || '');

  if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Currency must be a three-letter code.');
  if (!SETTLEMENT_CADENCES.includes(settlementCadence)) {
    throw new Error('Settlement cadence is invalid.');
  }
  if (!SUPPORTED_SETTLEMENT_CADENCES.includes(settlementCadence)) {
    throw new Error('Only immediate settlement cadence is currently supported.');
  }
  if (typeof policy?.transferTax !== 'boolean') {
    throw new Error('Tax-transfer policy must be true or false.');
  }
  if (typeof policy?.showPaymentProgressPercent !== 'boolean') {
    throw new Error('Payment-progress percentage policy must be true or false.');
  }
  for (const share of [payerProcessorFeeShareBps, operatorProcessorFeeShareBps]) {
    if (!Number.isInteger(share) || share < 0 || share > 10_000) {
      throw new Error('Processor-fee shares must be integer basis points from 0 to 10,000.');
    }
  }
  if (payerProcessorFeeShareBps + operatorProcessorFeeShareBps !== 10_000) {
    throw new Error('Processor-fee shares must total 10,000 basis points.');
  }
  const feePolicy = feeQuotePolicyById(processorFeeQuotePolicyId);
  if (
    processorFeeQuotePolicyId !== UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID &&
    (!feePolicy || feePolicy.currency !== currency)
  ) {
    throw new Error('Processor fee quote policy must match the project currency.');
  }

  return {
    currency,
    settlementCadence,
    transferTax: policy.transferTax,
    payerProcessorFeeShareBps,
    operatorProcessorFeeShareBps,
    processorFeeQuotePolicyId,
    showPaymentProgressPercent: policy.showPaymentProgressPercent,
  };
}

export function normalizedProjectPolicy(policy) {
  const currency = String(policy?.currency || 'CAD').trim().toUpperCase();
  return {
    currency,
    settlementCadence: 'immediate',
    transferTax: policy?.transferTax ?? true,
    payerProcessorFeeShareBps: policy?.payerProcessorFeeShareBps ?? 5000,
    operatorProcessorFeeShareBps: policy?.operatorProcessorFeeShareBps ?? 5000,
    showPaymentProgressPercent: policy?.showPaymentProgressPercent ?? true,
    processorFeeQuotePolicyId:
      feeQuotePolicyById(policy?.processorFeeQuotePolicyId)?.currency === currency
        ? policy.processorFeeQuotePolicyId
        : defaultFeeQuotePolicyId(currency),
  };
}
